import React, { useEffect, useRef } from 'react';
import {
  X, CheckCircle2, AlertTriangle, XCircle, Shield, Building2,
  Gauge, Activity,
} from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';

const COMPLIANCE_ENTRIES = [
  {
    key: 'compliant',
    label: 'Compliant',
    icon: CheckCircle2,
    tone: 'green',
    summary: 'Average income at or below 60% with at least 40% of units qualified.',
    body: 'The property meets the LIHTC Average Income Test. All required set-asides are satisfied and the building-level average sits in a safe band.',
  },
  {
    key: 'at_risk',
    label: 'At Risk',
    icon: AlertTriangle,
    tone: 'orange',
    summary: 'Close to the 60% ceiling. A single move could push the average over.',
    body: 'Still technically compliant, but the margin is thin. Redesignations, move-outs, or recerts that swap a low-AMI tenant for a higher one could trigger a breach. Plan changes carefully and run the simulator before committing.',
  },
  {
    key: 'breach',
    label: 'Breach',
    icon: XCircle,
    tone: 'red',
    summary: 'Average above 60% or qualified units below the 40% minimum.',
    body: 'The property fails AIT. Correction is required, typically by qualifying additional units, redesignating to lower AMI bands, or removing the failing unit from the qualified group. The Available Unit Rule and next-available-unit logic may apply.',
  },
  {
    key: 'fixed_set_aside',
    label: 'Fixed Set-Aside',
    icon: Shield,
    tone: 'blue',
    summary: 'Property elected 20/50, 40/60, or 25/60 NYC, not AIT.',
    body: 'This property runs on a fixed set-aside test rather than the Average Income Test. The 60% average ceiling and AIT bands do not apply; compliance is measured against the elected set-aside instead.',
  },
  {
    key: 'no_data',
    label: 'No Data',
    icon: Building2,
    tone: 'gray',
    summary: 'Not enough designation data yet to evaluate.',
    body: 'Upload a rent roll or assign designations to units. Once enough units have a current_designation, AIT compliance can be calculated.',
  },
];

const FLEXIBILITY_ENTRIES = [
  {
    key: 'high',
    label: 'High',
    tone: 'green',
    body: 'Wide room to redesignate units without risking compliance. Multiple moves available before approaching the 60% ceiling.',
  },
  {
    key: 'moderate',
    label: 'Moderate',
    tone: 'blue',
    body: 'Some room for changes, but watch the average closely. A few safe moves remain. Bigger swings need simulation first.',
  },
  {
    key: 'constrained',
    label: 'Constrained',
    tone: 'orange',
    body: 'Limited options. Most changes could push the average over 60%. Only low-AMI redesignations are typically safe.',
  },
  {
    key: 'critical',
    label: 'Critical',
    tone: 'red',
    body: 'No safe moves available without exceeding the income limit. Any upward redesignation would breach compliance.',
  },
];

const PRESSURE_ENTRIES = [
  {
    key: 'low',
    label: 'Low',
    tone: 'green',
    body: 'Comfortable margin. Average is well below the 60% threshold. Plenty of headroom for normal turnover.',
  },
  {
    key: 'moderate',
    label: 'Moderate',
    tone: 'orange',
    body: 'Getting closer to the limit. Plan redesignations carefully and avoid stacking higher-AMI moves.',
  },
  {
    key: 'elevated',
    label: 'Elevated',
    tone: 'orange',
    body: 'Near the threshold. One bad move could trigger non-compliance. Treat new certifications as high-impact decisions.',
  },
  {
    key: 'critical',
    label: 'Critical',
    tone: 'red',
    body: 'At or above 60%. Immediate action needed to restore compliance. Identify correction pathways for any over-income units.',
  },
];

const TONES = {
  green:  { text: 'text-green-600 dark:text-green-400',  bg: 'bg-green-100 dark:bg-green-900/30',   iconColor: 'text-green-500' },
  blue:   { text: 'text-blue-600 dark:text-blue-400',    bg: 'bg-blue-100 dark:bg-blue-900/30',     iconColor: 'text-blue-500' },
  orange: { text: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/30', iconColor: 'text-orange-500' },
  red:    { text: 'text-red-600 dark:text-red-400',      bg: 'bg-red-100 dark:bg-red-900/30',       iconColor: 'text-red-500' },
  gray:   { text: 'text-gray-600 dark:text-gray-300',    bg: 'bg-gray-100 dark:bg-gray-700/40',     iconColor: 'text-gray-400' },
};

const TITLES = {
  compliance: 'Compliance Status',
  'flexibility-pressure': 'Flexibility & Pressure',
};

const SUBTITLES = {
  compliance: 'How JPRO classifies AIT compliance position at the property level.',
  'flexibility-pressure': 'How JPRO measures room to maneuver and how close the building is to breaching the 60% ceiling.',
};

export default function AitGlossaryModal({ isOpen, topic, onClose }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderEntry = (entry, withIcon = false) => {
    const tone = TONES[entry.tone] || TONES.gray;
    const Icon = entry.icon;
    return (
      <li key={entry.key} className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-2 mb-1">
          {withIcon && Icon && (
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${tone.bg}`}>
              <Icon size={13} className={tone.iconColor} />
            </span>
          )}
          <span className={`text-sm font-medium capitalize ${tone.text}`}>{entry.label}</span>
        </div>
        {entry.summary && (
          <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>{entry.summary}</p>
        )}
        <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{entry.body}</p>
      </li>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div
        ref={panelRef}
        className={`w-full sm:w-[420px] h-full flex flex-col shadow-2xl border-l ${
          isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        <div className={`flex items-start justify-between px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{TITLES[topic] || 'Glossary'}</h3>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{SUBTITLES[topic]}</p>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-md ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {topic === 'compliance' && (
            <ul className="space-y-2">
              {COMPLIANCE_ENTRIES.map((e) => renderEntry(e, true))}
            </ul>
          )}

          {topic === 'flexibility-pressure' && (
            <>
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <Gauge size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                  <h4 className={`text-xs uppercase tracking-wide font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Flexibility</h4>
                </div>
                <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  How much room is left to move units across AMI bands before the building average exceeds 60%.
                </p>
                <ul className="space-y-2">
                  {FLEXIBILITY_ENTRIES.map((e) => renderEntry(e))}
                </ul>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-2">
                  <Activity size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                  <h4 className={`text-xs uppercase tracking-wide font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Pressure</h4>
                </div>
                <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  How close the current average sits to the 60% compliance ceiling.
                </p>
                <ul className="space-y-2">
                  {PRESSURE_ENTRIES.map((e) => renderEntry(e))}
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
